import { type ComputedRef, computed, type Ref, ref } from "vue";

import { useApi } from "./useApi";

export type UserRole = "manager" | "owner" | "staff";

export interface AuthenticatedUser {
  displayName: string;
  emailAddress: string;
  id: number;
  role: UserRole;
}

const ROLE_RANKS: Record<UserRole, number> = {
  manager: 2,
  owner: 3,
  staff: 1,
};

export function hasRoleAtLeast(role: UserRole | null | undefined, minimum: UserRole): boolean {
  if (!role) {
    return false;
  }
  return ROLE_RANKS[role] >= ROLE_RANKS[minimum];
}

export interface AuthenticationController {
  can: (action: "deleteContent" | "editCatalog" | "manageSettings" | "manageUsers") => boolean;
  currentUser: Ref<AuthenticatedUser | null>;
  error: Ref<Error | null>;
  hasResolvedSession: Ref<boolean>;
  isAuthenticated: ComputedRef<boolean>;
  isLoading: Ref<boolean>;
  loadCurrentUser: () => Promise<AuthenticatedUser | null>;
  logIn: (emailAddress: string, password: string) => Promise<AuthenticatedUser>;
  logOut: () => Promise<void>;
}

const currentUser = ref<AuthenticatedUser | null>(null);
const hasResolvedSession = ref(false);

export function useAuthentication(): AuthenticationController {
  const api = useApi();
  const error = ref<Error | null>(null);
  const isLoading = ref(false);

  const isAuthenticated = computed(() => currentUser.value !== null);

  async function loadCurrentUser(): Promise<AuthenticatedUser | null> {
    isLoading.value = true;
    error.value = null;
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? "";
      const response = await fetch(`${apiBaseUrl}/api/internal/me`, {
        credentials: apiBaseUrl ? "include" : "same-origin",
      });

      if (response.status === 401) {
        currentUser.value = null;
        hasResolvedSession.value = true;
        return null;
      }

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const body = (await response.json()) as {
        user: AuthenticatedUser;
      };
      currentUser.value = body.user;
      hasResolvedSession.value = true;
      return body.user;
    } catch (caughtError) {
      const normalizedError = caughtError instanceof Error ? caughtError : new Error(String(caughtError));
      error.value = normalizedError;
      currentUser.value = null;
      hasResolvedSession.value = true;
      throw normalizedError;
    } finally {
      isLoading.value = false;
    }
  }

  async function logIn(emailAddress: string, password: string): Promise<AuthenticatedUser> {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await api.post<{
        user: AuthenticatedUser;
      }>("/internal/login", {
        emailAddress,
        password,
      });
      currentUser.value = response.user;
      hasResolvedSession.value = true;
      return response.user;
    } catch (caughtError) {
      const normalizedError = caughtError instanceof Error ? caughtError : new Error(String(caughtError));
      error.value = normalizedError;
      throw normalizedError;
    } finally {
      isLoading.value = false;
    }
  }

  async function logOut(): Promise<void> {
    isLoading.value = true;
    error.value = null;
    try {
      await api.post("/internal/logout");
      currentUser.value = null;
    } catch (caughtError) {
      const normalizedError = caughtError instanceof Error ? caughtError : new Error(String(caughtError));
      error.value = normalizedError;
      throw normalizedError;
    } finally {
      isLoading.value = false;
    }
  }

  function can(action: "deleteContent" | "editCatalog" | "manageSettings" | "manageUsers"): boolean {
    const role = currentUser.value?.role ?? null;
    if (action === "manageSettings" || action === "manageUsers") {
      return hasRoleAtLeast(role, "owner");
    }
    return hasRoleAtLeast(role, "manager");
  }

  return {
    can,
    currentUser,
    error,
    hasResolvedSession,
    isAuthenticated,
    isLoading,
    loadCurrentUser,
    logIn,
    logOut,
  };
}
